import React from 'react';

/* ─── Syntax‐highlight tokens (VS Code Dark+ palette) ─── */
const cm = '#546E3F';
const kw = '#6FB3F2';
const fn = '#DCDCAA';
const pr = '#9CDCFE';
const st = '#CE9178';
const nm = '#B5CEA8';

const S = ({ c, children }: { c: string; children: React.ReactNode }) => (
  <span style={{ color: c }}>{children}</span>
);

const strategyCode: React.ReactNode[] = [
  <S c={cm}>{'/**'}</S>,
  <S c={cm}>{' * Define strategy parameters'}</S>,
  <S c={cm}>{' * Called once when strategy is loaded'}</S>,
  <S c={cm}>{' */'}</S>,
  <><S c={kw}>function</S> <S c={fn}>define</S>(<S c={pr}>ctx</S>) {'{'}</>,
  <>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>param</S>(<S c={st}>&apos;fastLength&apos;</S>, {'{'}</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>type</S>: <S c={st}>&apos;int&apos;</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>label</S>: <S c={st}>&apos;Fast EMA Length&apos;</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>default</S>: <S c={nm}>9</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>min</S>: <S c={nm}>5</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>max</S>: <S c={nm}>50</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>step</S>: <S c={nm}>5</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>optimize</S>: <S c={kw}>true</S>,</>,
  <>&nbsp;&nbsp;{'}'});</>,
  '',
  <>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>param</S>(<S c={st}>&apos;slowLength&apos;</S>, {'{'}</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>type</S>: <S c={st}>&apos;int&apos;</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>label</S>: <S c={st}>&apos;Slow EMA Length&apos;</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>default</S>: <S c={nm}>21</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>min</S>: <S c={nm}>10</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>max</S>: <S c={nm}>200</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>step</S>: <S c={nm}>10</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>optimize</S>: <S c={kw}>true</S>,</>,
  <>&nbsp;&nbsp;{'}'});</>,
  <>{'}'}</>,
  '',
  <S c={cm}>{'// Entry logic'}</S>,
  <><S c={kw}>if</S> (<S c={fn}>emaCrossUp</S>(<S c={pr}>ctx</S>)) {'{'}</>,
  <>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>enterLong</S>()</>,
  <>{'}'}</>,
];

const IDEPreviewHero = () => (
  <div className="rounded-lg overflow-hidden shadow-lg shadow-black/10 font-mono text-[13px] leading-[1.6]">
    <div className="flex" style={{ background: '#1e1e2e' }}>

      {/* ── Sidebar ── */}
      <div className="hidden md:flex flex-col w-[180px] flex-shrink-0 border-r border-white/[0.06] select-none" style={{ background: '#181825' }}>
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
          <span className="text-[10px] uppercase tracking-widest text-gray-500">Explorer</span>
          <span className="text-gray-600 text-[12px] cursor-default">&times;</span>
        </div>

        <div className="px-2 pt-2.5 pb-1">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
              <span className="text-[11px] text-gray-400">Files</span>
            </div>
            <span className="text-gray-600 text-[13px] leading-none cursor-default">+</span>
          </div>
          <div className="flex items-center gap-1.5 px-1.5 py-1 rounded bg-white/[0.06]">
            <svg className="w-3 h-3 text-blue-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            <span className="text-[11px] text-gray-300">strategy.qsl</span>
            <span className="text-[9px] bg-blue-500/20 text-blue-300 px-1 py-px rounded ml-auto">entry</span>
          </div>
        </div>

        <div className="px-2 pt-3 pb-2 border-t border-white/[0.04] mt-1">
          <div className="flex items-center gap-1 mb-2">
            <svg className="w-2.5 h-2.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9 5l7 7-7 7" /></svg>
            <span className="text-[11px] text-gray-400">History</span>
            <span className="text-[10px] text-gray-600 ml-0.5">v3</span>
          </div>
          <div className="pl-3 space-y-2.5">
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5"><span className="text-[10px] text-gray-500">v1</span></div>
              <p className="text-[9px] text-gray-600 leading-snug">Initial strategy</p>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                <span className="text-[9px] text-gray-600">2/15/2026</span>
              </div>
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5"><span className="text-[10px] text-gray-500">v2</span></div>
              <p className="text-[9px] text-gray-600 leading-snug">Tuned EMA bounds</p>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                <span className="text-[9px] text-gray-600">2/18/2026</span>
              </div>
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400">v3</span>
                <span className="text-[9px] bg-emerald-500/15 text-emerald-400 px-1 py-px rounded">Current</span>
              </div>
              <p className="text-[9px] text-gray-600 leading-snug">Added exit logic</p>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/50" />
                <span className="text-[9px] text-gray-600">2/20/2026</span>
              </div>
              <p className="text-[9px] text-gray-600">2 experiments</p>
              <p className="text-[9px] text-gray-600">Last run: 1h ago</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Editor area ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]" style={{ background: '#181825' }}>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-2 py-0.5 border-r border-white/[0.06] border-b-2 border-b-blue-400/60 -ml-2" style={{ background: '#1e1e2e' }}>
              <svg className="w-3 h-3 text-blue-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <span className="text-[11px] text-gray-300">strategy.qsl</span>
              <span className="text-gray-600 text-[11px] ml-1 cursor-default">&times;</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[9px] text-gray-600 select-none">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/70" />
              <span>Connected</span>
            </div>
            <span className="text-gray-700">|</span>
            <span>ema_cross</span>
            <span className="text-gray-700">|</span>
            <span>Last run: 12s ago</span>
          </div>
        </div>

        <div className="flex overflow-x-auto py-3" style={{ background: '#1e1e2e' }}>
          <div className="flex flex-col pr-4 pl-3 select-none flex-shrink-0 text-[12px] leading-[1.6] text-right" style={{ color: 'rgba(133,133,133,0.4)' }}>
            {strategyCode.map((_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </div>
          <div className="text-[12px] leading-[1.6] text-gray-300/90 pr-4 whitespace-pre">
            {strategyCode.map((line, i) => (
              <div
                key={i}
                className="min-h-[1.6em] -mx-1 px-1"
                style={{ background: i === 28 ? 'rgba(111,179,242,0.06)' : 'transparent' }}
              >
                {line}
                {i === 28 && <span className="inline-block w-[7px] h-[14px] bg-gray-300/60 ml-px align-middle" style={{ animation: 'blink 1s step-end infinite' }} />}
              </div>
            ))}
          </div>
        </div>
        <style>{`@keyframes blink { 50% { opacity: 0; } }`}</style>

        <div className="border-t border-white/[0.06]" style={{ background: '#181825' }}>
          <div className="flex items-center gap-1.5 px-3 py-1 border-b border-white/[0.04]">
            <svg className="w-2.5 h-2.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M5 15l7-7 7 7" /></svg>
            <span className="text-[10px] text-gray-500">Console</span>
          </div>
          <div className="px-3 py-2 space-y-0.5 text-[10px] font-mono">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Baseline Validation:</span>
              <span className="text-emerald-400">PASS</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Robustness Score:</span>
              <span className="text-blue-400">0.61</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Assets Tested:</span>
              <span className="text-gray-400">12</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default IDEPreviewHero;

// Re-export code data + helpers for use by parent page
export { strategyCode, S, cm, kw, fn, pr, st, nm };
